import React, { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, AlertCircle, EyeOff } from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Badge } from "@cms/ui/components/badge";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { ConfirmDeleteDialog } from "@cms/ui/components/confirm-dialog";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { MenuModuleGuard, MenuModuleHeader } from "../components/menu-module-shell";
import { MenuItemDialog, type MenuItemDialogInitial } from "../components/menu-item-dialog";
import {
  fetchMenuModel, createItem, updateItem, deleteItem,
  priceSummary, ALLERGEN_LABELS, DIETARY_LABELS,
  type ItemFields, type MenuItem, type MenuModel,
} from "../lib/menu-module";
import type { ClientOutletContext } from "../lib/outlet-context";

function ItemsList({ api, refreshStatus }: Pick<ClientOutletContext, "api" | "refreshStatus">) {
  const [model, setModel] = useState<MenuModel | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [dialog, setDialog] = useState<{ item?: MenuItem } | null>(null); // {} create, { item } edit
  const [dialogError, setDialogError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<MenuItem | null>(null);

  const load = useCallback(() => {
    if (!api) return;
    fetchMenuModel(api)
      .then(setModel)
      .catch((err: Error) => setError(err.message));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (fields: ItemFields) => {
    if (!api) return;
    setBusy(true);
    setDialogError("");
    try {
      if (dialog?.item) {
        await updateItem(api, dialog.item.id, fields);
      } else {
        await createItem(api, fields);
      }
      setDialog(null);
      load();
      refreshStatus?.();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (item: MenuItem) => {
    if (!api) return;
    setBusy(true);
    setError("");
    try {
      await updateItem(api, item.id, { active: !item.active });
      load();
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!api || !deleteTarget) return;
    setBusy(true);
    setError("");
    try {
      await deleteItem(api, deleteTarget.id);
      setDeleteTarget(null);
      load();
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };


  const items = model?.items ?? null;
  const dialogInitial: MenuItemDialogInitial | null = dialog?.item
    ? {
      ...dialog.item,
      imageUrl: dialog.item.image_r2_key && model?.assetsBaseUrl
        ? `${model.assetsBaseUrl}/${dialog.item.image_r2_key}`
        : null,
    }
    : null;

  return (
    <div>
      <MenuModuleHeader
        action={
          <Button size="sm" onClick={() => { setDialogError(""); setDialog({}); }}>
            <Plus className="h-4 w-4" />
            Add item
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!items && !error && <LoadingSpinner className="py-20" />}

      {items && items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No items yet — add your dishes here, then place them on menus.
          </CardContent>
        </Card>
      )}

      {items && items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Item</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">On menus</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        {item.image_r2_key && model?.assetsBaseUrl && (
                          <img
                            src={`${model.assetsBaseUrl}/${item.image_r2_key}`}
                            alt=""
                            className="h-8 w-8 shrink-0 rounded object-cover"
                          />
                        )}
                        <span className="font-medium">{item.name}</span>
                        {!item.active && (
                          <Badge variant="secondary" className="gap-1">
                            <EyeOff className="h-3 w-3" />
                            Hidden
                          </Badge>
                        )}
                      </span>
                      {(item.allergens.length > 0 || item.dietary_tags.length > 0) && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {[
                            ...item.dietary_tags.map((tag) => DIETARY_LABELS[tag] ?? tag),
                            ...item.allergens.map((tag) => ALLERGEN_LABELS[tag] ?? tag),
                          ].join(" · ")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{priceSummary(item)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {item.menu_names?.length ? item.menu_names.join(", ") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" disabled={busy} onClick={() => toggleActive(item)}>
                          {item.active ? "Hide" : "Show"}
                        </Button>
                        <Button
                          variant="ghost" size="sm" aria-label="Edit item"
                          onClick={() => { setDialogError(""); setDialog({ item }); }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm" aria-label="Delete item"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteTarget(item)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <MenuItemDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        onSave={save}
        initial={dialogInitial}
        allergens={model?.allergens ?? Object.keys(ALLERGEN_LABELS)}
        dietaryTags={model?.dietaryTags ?? Object.keys(DIETARY_LABELS)}
        api={api}
        saving={busy}
        error={dialogError}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.name}"?`}
        description={
          <>
            {deleteTarget?.menu_names?.length
              ? `This removes the dish from ${deleteTarget.menu_names.join(", ")} and deletes it from your items list. `
              : "This deletes the dish from your items list. "}
            Use Hide instead if it's only off today.
          </>
        }
        confirmLabel="Delete item"
        busy={busy}
        onConfirm={remove}
      />
    </div>
  );
}

export function MenuItemsPage() {
  return (
    <MenuModuleGuard title="Menu" description="Your item library.">
      {(ctx) => <ItemsList api={ctx.api} refreshStatus={ctx.refreshStatus} />}
    </MenuModuleGuard>
  );
}
